import { useState } from "react";
import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useCostCodes,
  useCreateCostCode,
  useSeedDefaultCodes,
  useDeleteCostCode,
  useUpdateCostCode,
  type CostCode,
  type CreateCostCodeData,
} from "@/hooks/useCostCodes";

export const Route = createFileRoute("/settings/cost-codes" as any)({
  component: CostCodesSettingsPage,
});

function CostCodesSettingsPage() {
  const { data, isLoading } = useCostCodes();
  const createCostCode = useCreateCostCode();
  const seedDefaultCodes = useSeedDefaultCodes();
  const deleteCostCode = useDeleteCostCode();
  const updateCostCode = useUpdateCostCode();
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editingCode, setEditingCode] = useState<CostCode | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [codeToDelete, setCodeToDelete] = useState<CostCode | null>(null);

  const costCodes = data?.costCodes || [];

  const createForm = useForm<CreateCostCodeData>({
    initialValues: {
      code: "",
      name: "",
      division: "",
    },
    validate: {
      code: (value) => (!value ? "Code is required" : null),
      name: (value) => (!value ? "Name is required" : null),
      division: (value) => (!value ? "Division is required" : null),
    },
  });

  const editForm = useForm<CreateCostCodeData>({
    initialValues: {
      code: "",
      name: "",
      division: "",
    },
    validate: {
      code: (value) => (!value ? "Code is required" : null),
      name: (value) => (!value ? "Name is required" : null),
      division: (value) => (!value ? "Division is required" : null),
    },
  });

  const handleCreate = createForm.onSubmit(async (values) => {
    try {
      await createCostCode.mutateAsync(values);
      notifications.show({
        title: "Success",
        message: "Cost code created successfully!",
        color: "green",
      });
      createForm.reset();
      closeCreate();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create cost code",
        color: "red",
      });
    }
  });

  const handleSeedDefaults = async () => {
    try {
      const result = await seedDefaultCodes.mutateAsync();
      notifications.show({
        title: "Success",
        message: `Seeded ${result.count} default CSI cost codes!`,
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to seed default codes",
        color: "red",
      });
    }
  };

  const handleEdit = (code: CostCode) => {
    setEditingCode(code);
    editForm.setValues({
      code: code.code,
      name: code.name,
      division: code.division,
    });
  };

  const handleEditSubmit = editForm.onSubmit(async (values) => {
    if (!editingCode) return;

    try {
      await updateCostCode.mutateAsync({
        id: editingCode.id,
        data: values,
      });
      notifications.show({
        title: "Success",
        message: "Cost code updated successfully!",
        color: "green",
      });
      setEditingCode(null);
      editForm.reset();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update cost code",
        color: "red",
      });
    }
  });

  const handleDeleteClick = (code: CostCode) => {
    setCodeToDelete(code);
    openDelete();
  };

  const handleDeleteConfirm = async () => {
    if (!codeToDelete) return;

    try {
      await deleteCostCode.mutateAsync(codeToDelete.id);
      notifications.show({
        title: "Success",
        message: "Cost code deleted successfully!",
        color: "green",
      });
      closeDelete();
      setCodeToDelete(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete cost code",
        color: "red",
      });
    }
  };

  // Group codes by division
  const groupedCodes = costCodes.reduce(
    (acc, code) => {
      if (!acc[code.division]) {
        acc[code.division] = [];
      }
      acc[code.division]!.push(code);
      return acc;
    },
    {} as Record<string, CostCode[]>
  );

  // Sort divisions
  const sortedDivisions = Object.keys(groupedCodes).sort((a, b) => {
    const aNum = parseInt(a.split(" ")[0] || "0");
    const bNum = parseInt(b.split(" ")[0] || "0");
    return aNum - bNum;
  });

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={2}>Company Cost Codes</Title>
                <Text size="sm" c="dimmed" mt="xs">
                  Manage your company's cost code catalog
                </Text>
              </div>
              <Group>
                {costCodes.length === 0 && (
                  <Button
                    variant="light"
                    onClick={handleSeedDefaults}
                    loading={seedDefaultCodes.isPending}
                  >
                    Seed Default Codes
                  </Button>
                )}
                <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                  Add Custom Code
                </Button>
              </Group>
            </Group>

            {costCodes.length === 0 ? (
              <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
                <Stack gap="md" align="center">
                  <Text c="dimmed">No cost codes in your catalog yet.</Text>
                  <Group>
                    <Button onClick={handleSeedDefaults} loading={seedDefaultCodes.isPending}>
                      Seed CSI Default Codes
                    </Button>
                    <Text c="dimmed">or</Text>
                    <Button variant="light" leftSection={<IconPlus size={16} />} onClick={openCreate}>
                      Add Custom Code
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            ) : (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {costCodes.length} cost {costCodes.length === 1 ? "code" : "codes"} (
                    {costCodes.filter((c) => c.isDefault).length} default,{" "}
                    {costCodes.filter((c) => !c.isDefault).length} custom)
                  </Text>
                </Group>

                <Accordion variant="contained" multiple defaultValue={sortedDivisions[0] ? [sortedDivisions[0]] : []}>
                  {sortedDivisions.map((division) => {
                    const divisionCodes = (groupedCodes[division] || []).sort((a, b) =>
                      a.code.localeCompare(b.code)
                    );

                    return (
                      <Accordion.Item key={division} value={division}>
                        <Accordion.Control>
                          <Group justify="space-between">
                            <Text fw={500}>{division}</Text>
                            <Badge variant="light" size="sm">
                              {divisionCodes.length}
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Table striped highlightOnHover>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Code</Table.Th>
                                <Table.Th>Name</Table.Th>
                                <Table.Th style={{ width: "100px" }}>Type</Table.Th>
                                <Table.Th style={{ width: "80px" }}></Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {divisionCodes.map((code) => (
                                <Table.Tr key={code.id}>
                                  <Table.Td>
                                    <Text size="sm" fw={500}>
                                      {code.code}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm">{code.name}</Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Badge
                                      variant="light"
                                      color={code.isDefault ? "blue" : "green"}
                                      size="sm"
                                    >
                                      {code.isDefault ? "Default" : "Custom"}
                                    </Badge>
                                  </Table.Td>
                                  <Table.Td>
                                    {!code.isDefault && (
                                      <Group gap="xs">
                                        <ActionIcon
                                          variant="subtle"
                                          onClick={() => handleEdit(code)}
                                        >
                                          <IconEdit size={16} />
                                        </ActionIcon>
                                        <ActionIcon
                                          color="red"
                                          variant="subtle"
                                          onClick={() => handleDeleteClick(code)}
                                        >
                                          <IconTrash size={16} />
                                        </ActionIcon>
                                      </Group>
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Create Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title="Add Custom Cost Code" centered>
        <form onSubmit={handleCreate}>
          <Stack gap="md">
            <TextInput
              label="Division"
              placeholder="e.g., 16 - Electrical"
              {...createForm.getInputProps("division")}
              required
            />

            <TextInput
              label="Code"
              placeholder="e.g., 16 05 00"
              {...createForm.getInputProps("code")}
              required
            />

            <TextInput
              label="Name"
              placeholder="e.g., Common Work Results for Electrical"
              {...createForm.getInputProps("name")}
              required
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeCreate} disabled={createCostCode.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={createCostCode.isPending}>
                Create Cost Code
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={!!editingCode}
        onClose={() => {
          setEditingCode(null);
          editForm.reset();
        }}
        title="Edit Cost Code"
        centered
      >
        <form onSubmit={handleEditSubmit}>
          <Stack gap="md">
            <TextInput
              label="Division"
              placeholder="e.g., 16 - Electrical"
              {...editForm.getInputProps("division")}
              required
            />

            <TextInput
              label="Code"
              placeholder="e.g., 16 05 00"
              {...editForm.getInputProps("code")}
              required
            />

            <TextInput
              label="Name"
              placeholder="e.g., Common Work Results for Electrical"
              {...editForm.getInputProps("name")}
              required
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setEditingCode(null);
                  editForm.reset();
                }}
                disabled={updateCostCode.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateCostCode.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteOpened}
        onClose={() => {
          closeDelete();
          setCodeToDelete(null);
        }}
        title="Delete Cost Code"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the cost code &quot;{codeToDelete?.code} - {codeToDelete?.name}&quot;? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                closeDelete();
                setCodeToDelete(null);
              }}
              disabled={deleteCostCode.isPending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteConfirm} loading={deleteCostCode.isPending}>
              Delete Cost Code
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
